const FENCE = '```';

export const CODE_FORMATS = [
  {id: 'curl', label: 'cURL'},
  {id: 'node', label: 'JavaScript'},
  {id: 'python', label: 'Python'},
  {id: 'php', label: 'PHP'},
];

const shellQuote = value => `'${String(value).replace(/'/g, `'\\''`)}'}`;

const headersToObject = headers => {
  if (!headers) return {};
  if (Array.isArray(headers)) {
    return Object.fromEntries(headers.map(([key, value]) => [key, value]));
  }
  return {...headers};
};

const bodyAsText = call => {
  if (call.requestBody == null) return null;
  if (typeof call.requestBody === 'string') return call.requestBody;
  return JSON.stringify(call.requestBody, null, 2);
};

/** Keeps prompts small enough to paste into a chat window. */
const MAX_SAMPLE_CHARS = 6000;

const jsonFence = value => {
  const serialized = JSON.stringify(value, null, 2);
  if (serialized.length <= MAX_SAMPLE_CHARS) {
    return `${FENCE}json\n${serialized}\n${FENCE}`;
  }
  return `${FENCE}json\n${serialized.slice(0, MAX_SAMPLE_CHARS)}\n${FENCE}\n\n(Response shortened — the full payload is longer but follows the same shape.)`;
};

const describeStatus = call => {
  if (call.pending) return 'pending';
  if (call.error) return `failed — ${call.error}`;
  return `${call.status ?? 'unknown'}${call.ok === false ? ' (error)' : ''}`;
};

const formatPathParams = call => {
  if (call.params == null) return null;
  return typeof call.params === 'object' ? JSON.stringify(call.params) : String(call.params);
};

/** Plain description of the captured call — shared by every prompt variant. */
const buildCallDescription = call => {
  const method = (call.method || 'GET').toUpperCase();
  const url = call.url || '';
  const headers = headersToObject(call.requestHeaders);
  const body = bodyAsText(call);
  const pathParams = formatPathParams(call);
  const duration = call.durationMs != null ? `${call.durationMs} ms` : 'unknown';

  const sections = [
    `### Endpoint\n${method} ${url}${
      pathParams ? `\n\nPath segment appended to the base URL: \`${pathParams}\`` : ''
    }`,
    `### Request options\n${
      Object.keys(headers).length
        ? `Headers:\n${jsonFence(headers)}`
        : 'Headers: none beyond what the browser sends by default.'
    }${body ? `\n\nRequest body:\n${FENCE}json\n${body}\n${FENCE}` : '\n\nRequest body: none.'}`,
    `### Response\nStatus: ${describeStatus(call)} · ${duration}`,
  ];

  if (call.error) {
    sections.push(`Error:\n${FENCE}\n${call.error}\n${FENCE}`);
  } else if (call.response != null) {
    const truncated = call.response?._truncated
      ? '\n\n(Sample truncated — real responses can be larger.)'
      : '';
    sections.push(`Body:\n${jsonFence(call.response)}${truncated}`);
  } else {
    sections.push('Body: not captured.');
  }

  return sections.join('\n\n');
};

export const buildAgentPrompt = call => {
  if (!call) return '';

  return `I captured this API request from Setupad's Client UI.

${buildCallDescription(call)}`;
};

export const buildCodeSnippet = (call, format = 'curl') => {
  if (!call) return '';

  const method = (call.method || 'GET').toUpperCase();
  const url = call.url || '';
  const headers = headersToObject(call.requestHeaders);
  const body = bodyAsText(call);

  switch (format) {
    case 'node': {
      const headerEntries = Object.entries(headers)
        .map(([key, value]) => `    ${JSON.stringify(key)}: ${JSON.stringify(value)},`)
        .join('\n');
      const bodyArg =
        typeof call.requestBody === 'object' && call.requestBody !== null
          ? `\n  body: JSON.stringify(${JSON.stringify(call.requestBody, null, 2).replace(
              /\n/g,
              '\n  '
            )}),`
          : body
            ? `\n  body: ${JSON.stringify(body)},`
            : '';
      return `const response = await fetch(${JSON.stringify(url)}, {
  method: ${JSON.stringify(method)},${
    headerEntries ? `\n  headers: {\n${headerEntries}\n  },` : ''
  }${bodyArg}
});

if (!response.ok) {
  throw new Error(\`Request failed: \${response.status}\`);
}

const data = await response.json();`;
    }

    case 'python': {
      const headerEntries = Object.entries(headers)
        .map(([key, value]) => `        ${JSON.stringify(key)}: ${JSON.stringify(value)},`)
        .join('\n');
      const bodyArg =
        body && typeof call.requestBody === 'object'
          ? `,\n    json=${JSON.stringify(call.requestBody, null, 4).replace(/\n/g, '\n    ')}`
          : body
            ? `,\n    data=${JSON.stringify(body)}`
            : '';
      return `import requests

response = requests.request(
    ${JSON.stringify(method)},
    ${JSON.stringify(url)},${
      headerEntries ? `\n    headers={\n${headerEntries}\n    }` : ''
    }${bodyArg}
)

response.raise_for_status()
data = response.json()`;
    }

    case 'php': {
      const headerLines = Object.entries(headers)
        .map(([key, value]) => `    ${JSON.stringify(`${key}: ${value}`)},`)
        .join('\n');
      return `<?php
${
  body
    ? `
$payload = <<<'JSON'
${body}
JSON;
`
    : ''
}
$ch = curl_init(${JSON.stringify(url)});

curl_setopt($ch, CURLOPT_CUSTOMREQUEST, ${JSON.stringify(method)});${
        headerLines ? `\ncurl_setopt($ch, CURLOPT_HTTPHEADER, [\n${headerLines}\n]);` : ''
      }${body ? '\ncurl_setopt($ch, CURLOPT_POSTFIELDS, $payload);' : ''}
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
curl_close($ch);

$data = json_decode($response, true);`;
    }

    case 'curl':
    default: {
      const parts = [`curl -X ${method} ${shellQuote(url)}`];
      Object.entries(headers).forEach(([key, value]) => {
        parts.push(`  -H ${shellQuote(`${key}: ${value}`)}`);
      });
      if (body) {
        parts.push(`  -d ${shellQuote(body)}`);
      }
      return parts.join(' \\\n');
    }
  }
};
